import React, { ChangeEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';

// redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setAnnouncement,
  setAnnouncementForm,
  setAnnouncementsTemplates,
  setAnnouncementTemplateForm,
} from 'redux/actions/announcementActions';
import { setRefresh } from 'redux/actions/pageActions';

// axios
import axios from 'axios';
import { apiClient } from "api-client"

// language
import { injectIntl, IntlShape } from 'react-intl';

// compoments
import RemoveItem from 'components-lib/RemoveItem';
import LoadingComponent from 'components/LoadingComponent';
import ErrorComponent from 'components/ErrorComponent';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// @mui material components
import Divider from '@mui/material/Divider';

// icons
import {
  announcementsWhiteIcon,
  editAnnoucementIcon,
} from 'assets/icons'

// sanitize
import DOMPurify from "dompurify";

// notifications
import { useSnackbar } from "notistack";

// types
import { AnnouncementsTemplate } from 'types/types';
import {
  AnnouncementDataForm,
  AnnouncementData,
  AnnouncementDataTemplates,
  AnnouncementDataTemplateForm,
  Refresh
} from 'types/typesRedux';

type Props = {
  intl: IntlShape,
}

const Announcement = ( {intl}: Props ) => {

  const dispatch = useDispatch();

  const navigate = useNavigate(); 

  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const announcement = useSelector( (state: AnnouncementData) => state.appAnnouncement.announcement );
  const announcementForm = useSelector( (state: AnnouncementDataForm) => state.appAnnouncement.announcementForm );
  const announcementTemplates = useSelector( (state: AnnouncementDataTemplates) => state.appAnnouncement.announcementsTemplates );
  const announcementTemplateForm = useSelector( (state: AnnouncementDataTemplateForm) => state.appAnnouncement.announcementTemplateForm );
  const refresh = useSelector( (state: Refresh) => state.handleRefresh);

  // hooks
  // const [ isAnnouncement, setIsAnnouncement ] = useState( false );
  const [ isRemove, setIsRemove ] = useState( false );
  const [isDisabled, setIsDisabled] = useState(true);
  const [isEditTemplate, setIsEditTemplate] = useState(false);

  const isDisabledEditBtn = announcementTemplates.some(template => 
    template.id === announcementTemplateForm.id && template.content === announcementTemplateForm.content
  );

  const [ localLoading, setLocalLoading ] = useState({
    announcement: true,
    templates: true,
  });
  
  const [ isError, setIsError ] = useState({
    announcement: false,
    templates: false,
  });

  const handleLoading = ( ifBool: boolean, setBool: boolean, name: keyof typeof localLoading ) => {
    if (localLoading[name] === ifBool) {setLocalLoading((prev) => ({
      ...prev,
      [name]: setBool,
      }));
    };
  }

  const handleError = ( ifBool: boolean, setBool: boolean, name: keyof typeof isError ) => {
    if (isError[name] === ifBool) {setIsError((prev) => ({
      ...prev,
      [name]: setBool,
      }));
    };
  }

  const handleRefresh = ( ifBool: boolean, setBool: boolean ) => {
    if ( refresh.refreshAnnouncement === ifBool ) dispatch(setRefresh( { refreshAnnouncement: setBool } ));
  }

  const toggleDisabled = () => {
    if ( isDisabled === true ) {
      setIsDisabled(false);
    } else {
      dispatch(setAnnouncementForm({
        content: announcement.content
      })
      )
      setIsDisabled(true);
    }
  };


  // Get announcement
  const getAnnouncement = async () => {
    const cancelToken = axios.CancelToken.source()
    try{
      const response = await apiClient.get(`/v1/announcement`,{
          cancelToken:cancelToken.token
      });

      if (response) {
        dispatch(setAnnouncement( response.data ));
        handleLoading(true, false, 'announcement')
        handleError(true, false, 'announcement')
        // console.log(response)
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled")
      } else {
        handleError(false, true, 'announcement')
      }
    }
    handleRefresh(true, false)
    return () => cancelToken.cancel()
  }
  
  // Get all templates
  const getTemplates = async () => {
    const cancelToken = axios.CancelToken.source()
    try{
      const response = await apiClient.get(`/v1/announcement-templates`,{
        cancelToken:cancelToken.token
      });

      if (response) {
        dispatch(setAnnouncementsTemplates( response.data.items ));
        handleLoading(true, false, 'templates')
        handleError(true, false, 'templates')
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled")
      } else {
        handleError(false, true, 'templates')
      }
    }
    handleRefresh(true, false)
    return () => cancelToken.cancel()
  }

  // Handle delete announcement
  const deleteAnnouncement = async () => {
    const cancelToken = axios.CancelToken.source();
    try {  
      const response = await apiClient.delete(`/v1/announcement`,{
      cancelToken:cancelToken.token
      });
      if ( response ) {
        dispatch(setRefresh( { refreshAnnouncement: true } ));
        // setAnnouncementForm({ ...announcementForm, content: '' });
        // setAnnouncement({ ...announcement, content: '' });
        enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementAlert.remove.true'})}`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Error:', error);
      enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementAlert.remove.false'})}`, { variant: 'error' });
    }
    return () => cancelToken.cancel()
  }

  // Handle add announcement
  const addAnnouncement = async ( announcement: string ) => {
    const cancelToken = axios.CancelToken.source();
    try {
      await apiClient.put(`/v1/announcement`, {
        content: announcement,
        cancelToken:cancelToken.token
      }
      ).then( ( response ) => {
        enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementAlert.add.true'})}`, { variant: 'success' });
        dispatch(setRefresh( { refreshAnnouncement: true } ));
        setIsDisabled(true)
      });
    } catch ( error ) {
      console.log( 'err' );
      enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementAlert.add.false'})}`, { variant: 'error' });
    }    
    return () => cancelToken.cancel();  
  }

  const handleEditTemplateAnnouncement = async () => {
    const cancelToken = axios.CancelToken.source()

    try {
      await apiClient.patch(`/v1/announcement-templates/${announcementTemplateForm.id}`,{
        content: announcementTemplateForm.content,

        cancelToken:cancelToken.token
        }
      ).then( ( response ) => {
        if ( response ) {
        enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementTemplateAlert.edit.true'})}`, { variant: 'success' });
        handleCancelBtn()
        dispatch(setRefresh( { refreshAnnouncement: true } ));
      }
    });
    } catch ( error ){
        console.log( 'err' )
        enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementTemplateAlert.edit.false'})}`, { variant: 'error' });
      }

    return () => cancelToken.cancel()
  }

  // const addTemplate = async () => {
  //   const cancelToken = axios.CancelToken.source()

  //   try {
  //     await apiClient.post(`/v1/announcement-templates`,{
  //       content: 'Cheesecake cupcake wafer sesame snaps soufflé chocolate jelly-o cake. Toffee sugar plum gummi bears pastry icing candy donut. Cake sweet roll cheesecake pastry cheesecake sesame snaps cheesecake.',

  //       cancelToken:cancelToken.token
  //       }
  //     ).then( ( response ) => {
  //       if ( response ) {
  //       // enqueueSnackbar(`${intl.formatMessage({id:'userAlert.editUser.true'})}`, { variant: 'success' });
  //       // dispatch(deleteAllFilters());
  //       // handleProfilePage();
  //     }
  //   });
  //   } catch ( error ){
  //       console.log( 'err' )
  //       // enqueueSnackbar(`${intl.formatMessage({id:'userAlert.editUser.false'})}`, { variant: 'error' });
  //     }

  //   return () => cancelToken.cancel()
  // }

  useEffect(() => {
    getAnnouncement();
    getTemplates();
  }, []);

  useEffect(() => {
    if ( refresh.refreshAnnouncement === true ) {
      getAnnouncement();
      getTemplates();
    }
  }, [refresh.refreshAnnouncement]);

  useEffect(() => {
    dispatch(setAnnouncementForm({
      content: announcement.content
    })
    )
  },[announcement.content])

  // handle edit template announcement
  const handleEditTemplate = ( id: number, template: string ) => {
    if ( id && template ) {
      dispatch(setAnnouncementTemplateForm({
        id: id,
        content: template
      })
      )
      setIsEditTemplate(true)
    }
  }

  // handle close modal windows if click cancel
  const handleCloseWindow = ( bool: boolean ) => {
    if ( bool === false ) {
        // setIsAnnouncement( false );
        setIsRemove( false );
    }
  }

  // handle open modal window remove announcement
  const handleOpenRemoveModal = () => {
    setIsRemove( true );
  }

  // handle delete announcement and close modal window
  const handleRemoveAnnouncement = ( bool: boolean ) => {
    if ( bool === true ) {
      deleteAnnouncement();
      setIsRemove( false );
    }
  }

  // handle submit 
  const handleAddAnnouncement = ( type: 'new' | 'template', content?: string ) => {
    if ( type === 'new' && announcementForm.content ) {
      addAnnouncement( announcementForm.content );
    } else if ( type === 'template' && content ) {
      dispatch(setAnnouncementForm({
        content: content,
      })
      )
      addAnnouncement( content );
    } else {
      enqueueSnackbar(`${intl.formatMessage({id:'appAnnouncementAlert.emptyInput'})}`, { variant: 'error' });
    }
  }

  // set data from inputs to redux
  const handleInputDescription = ( e: ChangeEvent<HTMLInputElement>, newValue: string, type?: 'template' ) => {

    newValue = DOMPurify.sanitize( newValue );

    if ( type === 'template' ) {
      dispatch(setAnnouncementTemplateForm({
        ...announcementTemplateForm,
        content: newValue,
      })
      )
    } else {
      dispatch(setAnnouncementForm({
        content: newValue,
      })
      )
    }
  };

  const handleCancelBtn = () => {
    dispatch(setAnnouncementTemplateForm({
      id: null,
      content: null,
    })
    )
    setIsEditTemplate(false)
  }  

  console.log(announcement.content)

  if ( isError.announcement || isError.templates ) {
    return <ErrorComponent onPress={() => handleRefresh(false, true)} />;
  }

  if ( localLoading.announcement || localLoading.templates ) {
    return <LoadingComponent />;
  }

  return (
    <>
      <MDBox
        sx={{ marginLeft: "max(18.7rem)", paddingRight: "1rem", width: "auto",
          display: "flex", flexDirection: "column", marginTop: "4rem",
          marginBottom: "3rem", maxWidth: "82rem", gap: "3.8rem",
        }}
      >

        {/* current announcement */}
        <MDBox
          sx= {{ minWidth: "70%", width: "max-content", maxWidth: "100%", 
            display: "flex", flexDirection: "column", gap: "1rem",
            padding: "0.8rem 1.5rem", backgroundColor: "#ffffff",
            borderRadius: "0.75rem",
          }}
        >

          <MDBox
            sx={{
              display: "flex", flexDirection: " column",
              width: "100%",
            }}
          >
            <MDBox 
              sx= {{ 
                width: "100%", display: "flex", flexDirection: "row", 
                gap: "1rem",
              }}
            >
              <MDBox className= "badge"
                sx={{ 
                  // backgroundColor: "#17C063",
                  p: 1, mt: -4, ml: 0,
                  borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl, 
                }}
                display="grid"
                justifyContent="center"
                alignItems="center"
                bgColor="#17C063"
                color="white"
                width="4.5rem"
                height="4.5rem"
                shadow="md"
                borderRadius="lg"
                variant="gradient"
                fontSize= "2.2rem"
              >
                {announcementsWhiteIcon}
              </MDBox>
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <MDTypography
                  sx={{
                    fontSize: "1.2rem", fontWeight: "700",
                  }}
                >
                  {intl.formatMessage({id:'announcements.title.currentAnnouncement'})}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox
              sx={{
                display: "flex", flexDirection: "column",
                width: "100%", marginTop: "0.9rem",
                marginBottom: "0.9rem",
              }}
            >
              <MDBox sx={{ position: "relative", width: "100%" }}>
                <MDInput
                  className= "currentAnnouncementInput"
                  type="text"
                  name="description"
                  multiline
                  rows={4}
                  disabled={isDisabled}
                  value={!isDisabled
                    ? announcementForm?.content || ''
                    : announcement?.content || ''
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputDescription(e, e.target.value)}
                    inputProps={{
                      maxLength: 300,
                    }}
                  InputProps={{
                    style: {
                      backgroundColor: isDisabled ? "#D2D6DA" : "#FFF",
                      fontWeight: "700",
                    }
                  }}
                  style={{ width: "100%", color: "#7B809A", fontWeight: "700" }}
                />

                <MDBox 
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 8,
                    fontSize: "0.8rem",
                    color: "text.secondary", // prípadne uprav na konkrétnu farbu, napr. "#888"
                  }}
                >
                  {300 - (announcementForm?.content?.length || 0)}
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  display: "flex", flexDirection: "row",
                  justifyContent: "flex-end", width: "100%",
                  marginTop: "1.3rem", gap: "0.7rem"
                }}
              >
                { isDisabled === false && (
                  <>
                    <MDButton 
                      variant="contained" 
                      color="success"
                      onClick={() => toggleDisabled()}         
                    >
                      {intl.formatMessage({id:'button.cancel'})}
                    </MDButton>

                    <MDButton
                      variant="contained" 
                      color="success"
                      onClick={() => handleAddAnnouncement('new')}
                    >
                      {intl.formatMessage({id:'button.useAnnouncement'})}
                    </MDButton>
                  </>
                )}
                { isDisabled === true && (
                  <>
                    <MDButton
                      variant="contained"
                      color="success"
                      onClick={() => toggleDisabled()}
                    >
                      {announcementForm?.content ? intl.formatMessage({id:'button.changeCurrentAnnouncement'}) : intl.formatMessage({id:'button.addNewAnnouncement'})}
                    </MDButton>
                  </>
                )}
                  <>
                    { announcement?.content && (
                      <MDButton
                        variant="contained"
                        color="success" 
                        onClick={() => handleOpenRemoveModal()}   
                      >
                        {intl.formatMessage({id:'button.removeCurrentAnnouncement'})}
                      </MDButton>
                    )}
                    
                  </>
              </MDBox>
            </MDBox>

          </MDBox>
        </MDBox>

        {/* announcement + templates */}
        <MDBox
          sx= {{ minWidth: "70%", width: "max-content", maxWidth: "100%", 
            display: "flex", flexDirection: "column", gap: "1rem",
            padding: "0.8rem 1.5rem", paddingBottom: "2rem", 
            backgroundColor: "#ffffff", borderRadius: "0.75rem", 
          }}
        >

          <MDBox
            sx={{
              display: "flex", flexDirection: " column",
              width: "100%",
            }}
          >
            <MDBox 
              sx= {{ 
                width: "100%", display: "flex", flexDirection: "row", 
                gap: "1rem",
              }}
            >
              <MDBox className= "badge"
                sx={{
                  p: 1, mt: -4, ml: 0,
                  borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl, 
                }}
                display="grid"
                justifyContent="center"
                alignItems="center"
                bgColor="#17C063"
                color="white"
                width="4.5rem"
                height="4.5rem"
                shadow="md"
                borderRadius="lg"
                variant="gradient"  
                fontSize= "2.2rem"        
              >
                {announcementsWhiteIcon}
              </MDBox>
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <MDTypography
                  sx={{
                    fontSize: "1.2rem", fontWeight: "700",
                  }}
                >
                  {intl.formatMessage({id:'announcements.templates'})}
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* { isEditTemplate && (
              <> */}
                <MDBox
                  sx={{
                    display: "flex", flexDirection: "column",
                    width: "100%", marginTop: "0.9rem",
                    marginBottom: "0.9rem",
                    maxHeight: isEditTemplate ? "500px" : "0px",
                    overflow: "hidden",
                    transform: isEditTemplate ? "translateY(0)" : "translateY(20px)",
                    opacity: isEditTemplate ? 1 : 0,
                    transition: "max-height 0.4s ease-out, opacity 0.4s ease-out, transform 0.4s ease-out",
                  }}
                >
                  
                  <MDBox sx={{ position: "relative", width: "100%" }}>
                    <MDInput 
                      type="text"
                      name='description'
                      multiline
                      rows={4}
                      value={announcementTemplateForm?.content ?? ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputDescription(e, e.target.value, 'template')}
                      inputProps={{
                        maxLength: 300,
                      }}
                      sx={{ width: "100%" }}
                    />

                    <MDBox
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 8,
                        fontSize: "0.8rem",
                        color: "text.secondary", // prípadne uprav na konkrétnu farbu, napr. "#888"
                      }}
                    >
                      {300 - (announcementTemplateForm?.content?.length || 0)}
                    </MDBox>
                  </MDBox>

                  {/* <MDTypography
                    sx={{
                      marginTop: "0.2rem",
                      fontSize: "0.6rem", fontWeight: "400",
                    }}
                  >
                    {intl.formatMessage({id:'announcements.note'})}
                  </MDTypography> */}

                  <MDBox
                    sx={{
                      display: "flex", flexDirection: "row",
                      justifyContent: "flex-end", width: "100%",
                      marginTop: "1.3rem", gap: "0.7rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <MDButton 
                      variant="contained" 
                      color="success"
                      onClick={() => handleCancelBtn()}         
                    >
                      {intl.formatMessage({id:'button.cancel'})}
                    </MDButton>
                    <MDButton
                      variant="contained" 
                      color="success" 
                      disabled={isDisabledEditBtn}
                      onClick={() => handleEditTemplateAnnouncement()}      
                    >
                      {intl.formatMessage({id:'button.editAnnouncement'})}
                    </MDButton>
                  </MDBox>
                
                  <Divider
                    orientation="horizontal"
                    flexItem
                    sx={{ 
                      width: '100%', height: '1px', marginTop: "0.3rem", 
                      marginBottom: "0rem", background: 'black',
                    }}
                  />

                  <MDTypography
                    sx={{
                      fontSize: "0.75rem", fontWeight: "700",
                      color: "#7B809A", marginTop: "0.6rem",
                    }}
                  >
                    {intl.formatMessage({id:'announcements.templates'})}
                  </MDTypography>
                </MDBox>
              
              {/* </>
            )} */}


            {/* templates */}
            <MDBox
              sx={{
                width: "100%", marginTop: "0.6rem",
                display: "flex", flexDirection: "column",
              }}
            >
              <MDBox
                sx={{
                  display: "flex", flexDirection: "column",
                  gap: "0.7rem"
                }}
              >
                { announcementTemplates.map((template: AnnouncementsTemplate) => {
                  return (
                    <MDBox
                      sx={{
                        width: "100%", display: "flex",
                        flexDirection: "row", gap: "0.7rem",
                      }}
                    >
                      <MDBox
                        sx={{
                          display: "flex", flex: "1",
                          maxWidth: "100%",
                        }}
                      >
                        <MDInput
                          type="text"
                          name="description"
                          multiline
                          rows={1}
                          disabled
                          value={template?.content ?? ""}
                          sx={{
                            width: "100%",
                          }}
                          inputProps={{
                            style: {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              backgroundColor: "#D2D6DA",
                              color: "#807E7B",
                            },
                          }}
                        />
                      </MDBox>

                      <MDButton
                        variant="contained" 
                        color="success" 
                        onClick={() => handleAddAnnouncement('template', template?.content)}         
                      >
                        {intl.formatMessage({id:'button.useAnnouncement'})}
                      </MDButton>

                      <MDBox
                        sx={{
                          display: "flex", flexDirection: "row",
                          justifyContent: "center", alignItems: "center",
                          padding: "0 0.75rem", border: "1px solid #807E7B",
                          borderRadius: "0.25rem", cursor: "pointer",
                          boxShadow: "0rem 0.1875rem 0.1875rem 0rem rgba(76, 175, 80, 0.15),0rem 0.1875rem 0.0625rem -0.125rem rgba(76, 175, 80, 0.2),0rem 0.0625rem 0.3125rem 0rem rgba(76, 175, 80, 0.15)",

                          "&:hover": {
                            color: "#ffffff",
                            boxShadow:
                              "0rem 0.875rem 1.625rem -0.75rem rgba(76, 175, 80, 0.4)," +
                              "0rem 0.25rem 1.4375rem 0rem rgba(76, 175, 80, 0.15)," +
                              "0rem 0.5rem 0.625rem -0.3125rem rgba(76, 175, 80, 0.2)",
                          },

                          "&:active": {
                            opacity: "0.75",
                          },
                        }}
                        onClick={() => handleEditTemplate(template?.id, template?.content)}
                      >
                        {editAnnoucementIcon}
                      </MDBox>

                    </MDBox>
                  )
                })}
              </MDBox>              

            </MDBox>

          </MDBox>
        </MDBox>

        
      </MDBox>

      { isRemove && 
        <RemoveItem
          title={ 'announcement' }
          onCloseWindow={ handleCloseWindow }
          onRemoveItem={ handleRemoveAnnouncement }
        /> }  
    </>
  )
}

export default injectIntl(Announcement);